import { Box, Typography } from "@mui/material";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import useResponsive from "../../hooks/useResponsive";
import { forwardRef, useEffect, useRef } from "react";
import { PumpRecommendations } from "../../hooks/useFormPoolCalculations";
import { usePumpRecommendationContext } from "../../context/PumpRecommendationContext";
import { pdfGenerator } from "../../services/pdf";

export const EletricCostGraph = forwardRef<
  HTMLDivElement,
  {
    recommendations: PumpRecommendations;
  }
>(({ recommendations }, ref) => {
  const { isSm, isXs } = useResponsive();
  const { reportId, formData } = usePumpRecommendationContext();
  const pdfGeneratedRef = useRef(false);
  const isMobile = isSm || isXs;

  useEffect(() => {
    if (
      !reportId ||
      !ref ||
      typeof ref === "function" ||
      !formData ||
      pdfGeneratedRef.current
    )
      return;

    const timer = setTimeout(async () => {
      try {
        pdfGeneratedRef.current = true;

        const userData = {
          user: formData.user || "User",
          email: formData.email || "user@example.com",
          isExistingPool: formData.isExistingPool || false,
          latestRecommendationReportId: reportId,
        };

        await pdfGenerator.generatePDF({
          download: false, 
          isEmpty: false,
          graphRef: ref as React.RefObject<HTMLElement>,
          formData,
          recommendations,
          isMobile: isMobile,
          ...userData,
        });
      } catch (error) {
        console.error("Error generating and sending PDF:", error);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [reportId, ref, formData, recommendations, isMobile]);

  const isTheSamePump = recommendations.isTheSamePump;
  const temperatureArray = Object.values(recommendations.temperaturePerMonth);

  const graphData = isTheSamePump
    ? recommendations.monthsArray.map((month, index) => ({
        name: month.name.charAt(0).toUpperCase() + month.name.slice(1),
        "Option 1": month.value
          ? parseFloat(recommendations.standard.monthlyCosts[index].toFixed(2))
          : 0,
        "Average City Temp": month.value ? temperatureArray[index] : 0,
        "Heat demand": month.value
          ? parseFloat(recommendations.heatDemandLoss[index].toFixed(2))
          : 0,
      }))
    : recommendations.monthsArray.map((month, index) => ({
        name: month.name.charAt(0).toUpperCase() + month.name.slice(1),
        "Option 1": month.value
          ? parseFloat(recommendations.standard.monthlyCosts[index].toFixed(2))
          : 0,
        "Option 2": month.value
          ? parseFloat(recommendations.premium.monthlyCosts[index].toFixed(2))
          : 0,
        "Heat demand": month.value
          ? parseFloat(recommendations.heatDemandLoss[index].toFixed(2))
          : 0,
        "Average City Temp": month.value ? temperatureArray[index] : 0,
      }));

  const CustomizedTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: any[];
    label?: string;
  }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
          }}
        >
          <p className="label">{`${label}`}</p>
          {payload.map((entry, index) => {
            let unit = "";
            let value = entry.value;
            if (entry.name === "Average City Temp") {
              unit = "°C";
            } else if (entry.name === "Heat demand") {
              unit = " kW";
            } else {
              unit = "";
              value = `$${entry.value}`;
            }
            return (
              <p key={`item-${index}`} style={{ color: entry.color }}>
                {`${entry.name} : ${value}${unit}`}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <Box
      ref={isMobile ? null : ref}
      display={"flex"}
      flexDirection={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      width={isMobile ? 400 : "100%"}
    >
      <Typography
        my={isMobile ? 4 : 4}
        textAlign={isMobile ? "start" : "center"}
        fontWeight={"bold"}
        fontSize={28}
        padding={isMobile ? 4 : 0}
      >
        Estimated Electricity Cost & Heat Demand on the selected season
      </Typography>
      <Box ref={isMobile ? ref : null}>
        <ResponsiveContainer width={isMobile ? 400 : 1000} height={400}>
          <ComposedChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis
              label={{
                angle: 0,
                position: "insideLeft",
                dx: -10,
              }}
              domain={[0]}
            />
            <Tooltip content={<CustomizedTooltip />} />
            <Legend
              verticalAlign="top"
              align="right"
              wrapperStyle={{ margin: "-10px" }}
            />
            <Bar dataKey="Average City Temp" fill="#ffd200" />
            <Line type="monotone" dataKey="Heat demand" stroke="#ff7300" />
            <Line type="monotone" dataKey="Option 1" stroke="#8884d8" />
            {!isTheSamePump && (
              <Line type="monotone" dataKey="Option 2" stroke="#82ca9d" />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
});

EletricCostGraph.displayName = "EletricCostGraph";
